import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

import { useWebSocket } from './WebSocketProvider';
import { DataState } from './DataProvider';
import { fetchWorkflowExecutionsCurrent } from '../services/workflowApi';
const WorkflowExecutionsContext = createContext();

export const WorkflowExecutionsProvider = ({ children }) => {
  const { subscribe, isConnected } = useWebSocket();
  const { workflows, userGoogleId } = DataState();
  const [workflowExecutions, setWorkflowExecutions] = useState([]);

  // Create a map of workflow_id to workflowTitle using the workflows data.
  const workflowMap = workflows.reduce((acc, { id, title }) => {
    acc[id] = title;
    return acc;
  }, {});

  // This function handles incoming notifications.
  const handleNewBotStatusNotification = (notification) => {
    if (notification.workflow_id && notification.workflow_id in workflowMap) {
      setWorkflowExecutions((prevExecutions) => {
        // Copy the existing executions array.
        const executions = [...prevExecutions];
        // Find if the execution already exists.
        const executionIndex = executions.findIndex(
          (s) => s.execution_id === notification.execution_id,
        );

        if (executionIndex !== -1) {
          // Update the status if the execution exists.
          executions[executionIndex] = {
            ...executions[executionIndex],
            status: notification.status,
          };
        } else {
          // Add the new execution to the front.
          executions.unshift({
            workflow_id: notification.workflow_id,
            status: notification.status,
            execution_id: notification.execution_id,
            workflowTitle: workflowMap[notification.workflow_id],
          });
        }

        // If the list grows beyond 10 executions, remove the last execution with status "success".
        if (executions.length > 15) {
          for (let i = executions.length - 1; i >= 0; i--) {
            if (executions[i].status !== 'running') {
              executions.splice(i, 1);
              break; // Remove only one execution.
            }
          }
        }
        if (notification.status === 'running') {
          toast.success(
            `Workflow Execution Started: ${workflowMap[notification.workflow_id]}`,
          );
        } else if (notification.status === 'failed') {
          toast.error(
            `Workflow Execution Failed: ${workflowMap[notification.workflow_id]}`,
          );
        } else if (notification.status === 'success') {
          toast.success(
            `Workflow Execution Complete: ${workflowMap[notification.workflow_id]}`,
          );
        }
        return executions;
      });
    }
  };

  useEffect(() => {
    if (!isConnected) return;
    const eventKey = 'workflow_execution_status_notification';

    // Subscribe to the WebSocket event.
    const unsubscribe = subscribe({
      [eventKey]: (data) => {
        // toast.success(JSON.stringify(data));
        handleNewBotStatusNotification(data);
      },
    });

    // Cleanup on unmount or dependency change.
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line
  }, [subscribe, isConnected, workflows]);

  useEffect(() => {
    const setInitialWorkflowExecutions = async () => {
      try {
        const executionSessions =
          await fetchWorkflowExecutionsCurrent(userGoogleId);
        setWorkflowExecutions(
          executionSessions.map((execution) => ({
            ...execution,
            workflowTitle: execution.name,
          })),
        );
      } catch (error) {
        toast.error(error?.message || String(error));
      }
    };
    if (workflows && workflows.length > 0) {
      setInitialWorkflowExecutions();
    }
    // eslint-disable-next-line
  }, [workflows]);

  return (
    <WorkflowExecutionsContext.Provider value={{ workflowExecutions }}>
      {children}
    </WorkflowExecutionsContext.Provider>
  );
};

// Custom hook for easy consumption.
export const useWorkflowExecutions = () =>
  useContext(WorkflowExecutionsContext);
